import { useState, useCallback, useEffect, useRef } from "react";
import { usePost } from "./usePost";
import { usePoll } from "./usePoll";
import { API_ROUTES } from "../services/api/config";
import {
  AIJobStartResponse,
  AIJobStatusResponse,
  AIJobProgress,
} from "../types/compassAITypes";

const POLL_INTERVAL = 2000;

const MAX_POLL_ATTEMPTS = 300;

type JobStatus = AIJobStatusResponse["status"];

interface UseJobPollingReturn<TResult> {
  startJob: (
    triggerRoute: keyof typeof API_ROUTES,
    payload: Record<string, unknown>,
  ) => Promise<void>;
  stopJob: () => void;
  reset: () => void;
  jobId: string | null;
  status: JobStatus | null;
  progress: AIJobProgress | null;
  latestResponse: AIJobStatusResponse<TResult> | null;
  result: TResult | null;
  errorMessage: string | null;
  isPending: boolean;
  isRunning: boolean;
  isPolling: boolean;
  isComplete: boolean;
  isFailed: boolean;
}

export function useJobPolling<
  TResult = Record<string, unknown>,
>(): UseJobPollingReturn<TResult> {
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [progress, setProgress] = useState<AIJobProgress | null>(null);
  const [result, setResult] = useState<TResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const jobIdRef = useRef<string | null>(null);

  const { sendData: triggerJob } = usePost<
    AIJobStartResponse,
    Record<string, unknown>
  >();

  const {
    data: completedJob,
    latestResponse,
    error: pollError,
    isLoading: isPollingActive,
    startPolling,
    stopPolling,
  } = usePoll<AIJobStatusResponse<TResult>, { job_id: string }>(
    (response) => response.status === "COMPLETED",
    (response) => response.status === "FAILED",
    POLL_INTERVAL,
    MAX_POLL_ATTEMPTS,
  );

  useEffect(() => {
    if (latestResponse) {
      setStatus(latestResponse.status);

      setProgress({
        currentStep: latestResponse.current_step ?? 0,
        totalSteps: latestResponse.total_steps ?? 1,
        stepLabel: latestResponse.step_label ?? "",
        stepDetail: latestResponse.step_detail,
        percentage:
          latestResponse.total_steps > 0
            ? Math.round(
                (latestResponse.current_step / latestResponse.total_steps) *
                  100,
              )
            : 0,
      });
    }
  }, [latestResponse]);

  useEffect(() => {
    if (completedJob) {
      setStatus("COMPLETED");
      const payload = completedJob.result_payload;
      const isValidPayload =
        payload !== null &&
        typeof payload === "object" &&
        !Array.isArray(payload);
      setResult(isValidPayload ? (payload as TResult) : null);
      setProgress({
        currentStep: completedJob.total_steps,
        totalSteps: completedJob.total_steps,
        stepLabel: completedJob.step_label ?? "Done",
        stepDetail: null,
        percentage: 100,
      });
    }
  }, [completedJob]);

  useEffect(() => {
    if (pollError) {
      setStatus("FAILED");
      const failMessage =
        latestResponse?.error_message ||
        pollError.error_user_message ||
        pollError.error_string ||
        "An unexpected error occurred";
      setErrorMessage(failMessage);
    }
  }, [pollError]);

  const startJob = useCallback(
    async (
      triggerRoute: keyof typeof API_ROUTES,
      payload: Record<string, unknown>,
    ): Promise<void> => {
      setResult(null);
      setErrorMessage(null);
      setProgress(null);
      setStatus("PENDING");

      const jobResponse = await triggerJob(triggerRoute, payload);

      if (!jobResponse?.job_id) {
        setStatus("FAILED");
        setErrorMessage("Failed to start job — no job ID returned.");
        return;
      }

      setJobId(jobResponse.job_id);
      jobIdRef.current = jobResponse.job_id;

      startPolling("AI_GET_JOB_STATUS", { job_id: jobResponse.job_id });
    },
    [triggerJob, startPolling],
  );

  const stopJob = useCallback(() => {
    stopPolling();
    setStatus(null);
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setJobId(null);
    jobIdRef.current = null;
    setStatus(null);
    setProgress(null);
    setResult(null);
    setErrorMessage(null);
  }, [stopPolling]);

  return {
    startJob,
    stopJob,
    reset,
    jobId,
    status,
    progress,
    latestResponse,
    result,
    errorMessage,
    isPending: status === "PENDING",
    isRunning: status === "RUNNING",
    isPolling: isPollingActive,
    isComplete: status === "COMPLETED",
    isFailed: status === "FAILED",
  };
}
